<script setup lang="ts">
/**
 * 全域 Bottom Sheet 容器 (Google 級交互)
 * 解決方案：
 * 1. 支援 Touch Swipe 手勢關閉 (iOS 原生感)
 * 2. 處理 Body Scroll Lock (防止背景穿透捲動)
 * 3. RWD：手機版滑出，平板/電腦版轉為 Modal
 */
import { useSwipe } from "@vueuse/core";

defineProps<{ title?: string }>();

const isOpen = defineModel<boolean>({ default: false });
const sheetRef = ref<HTMLElement | null>(null);
// 手勢支援
const { lengthY } = useSwipe(sheetRef, {
  onSwipeEnd(e, direction) {
    if (direction === "down" && lengthY.value < -80) isOpen.value = false;
  },
});
// 捲動鎖定
watch(isOpen, (val) => {
  if (typeof window !== "undefined") {
    document.body.style.overflow = val ? "hidden" : "";
  }
});
</script>
<template>
  <ClientOnly>
    <Teleport to="body">
      <!-- 遮罩 -->
      <Transition name="fade">
        <div
          v-if="isOpen"
          class="fixed inset-0 z-[300] bg-slate-950/50 backdrop-blur-sm"
          @click="isOpen = false"
        ></div>
      </Transition>
      <!-- 面板 -->
      <Transition name="slide-up">
        <div
          v-if="isOpen"
          ref="sheetRef"
          class="fixed bottom-0 left-0 right-0 z-[301] max-h-[90dvh] w-full flex flex-col bg-white rounded-t-[2rem] shadow-2xl safe-area-bottom overflow-hidden border-t border-slate-100"
          role="dialog"
        >
          <!-- 指示條與標題 -->
          <div
            class="flex-shrink-0 pt-4 pb-4 px-8 flex flex-col items-center sticky top-0 bg-white z-10 border-b border-slate-50"
          >
            <div class="w-10 h-1 bg-slate-200 rounded-full mb-6"></div>
            <div class="w-full flex justify-between items-center">
              <h3
                class="text-xl font-black italic tracking-tighter uppercase text-slate-900"
              >
                {{ title }}
              </h3>
              <button
                @click="isOpen = false"
                class="flex p-2 bg-slate-100 text-slate-400 rounded-full hover:bg-slate-200 transition"
              >
                <Icon name="ph:x-bold" class="text-lg" />
              </button>
            </div>
          </div>
          <!-- 內容 -->
          <div
            class="p-8 overflow-y-auto overscroll-contain flex-1 custom-scrollbar"
          >
            <slot></slot>
          </div>
        </div>
      </Transition>
    </Teleport>
  </ClientOnly>
</template>
<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.5s cubic-bezier(0.32, 0.72, 0, 1);
}
.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
}
</style>
